import { ArrowRight } from 'lucide-react'
import Link from 'next/link'
import * as React from 'react'

import { cn } from '@/lib/utils'

export interface CommunityCTASectionProps {
  title: string
  description: string
  ctaLabel: string
  ctaUrl: string
  secondaryLabel?: string
  secondaryUrl?: string
  gradientFrom?: string
  gradientTo?: string
  className?: string
}

export const GENERAL_V1_COMMUNITY_CTA_GRADIENT = {
  from: '#83b7ff',
  to: '#b9d6ff',
} as const

/**
 * General V1 Community CTA — Figma node 1:1644.
 *
 * Inset 1232px card with blue gradient background, 80px padding, 20px radius.
 * Large centered heading (~80px Figma, we use 5xl/6xl), description,
 * and one primary "Join community" CTA. Secondary action disabled by default.
 */
export function CommunityCTASection({
  title,
  description,
  ctaLabel,
  ctaUrl,
  secondaryLabel,
  secondaryUrl,
  gradientFrom = GENERAL_V1_COMMUNITY_CTA_GRADIENT.from,
  gradientTo = GENERAL_V1_COMMUNITY_CTA_GRADIENT.to,
  className,
}: CommunityCTASectionProps) {
  const isCtaDisabled = !ctaUrl || ctaUrl === '#'
  const isSecondaryDisabled = !secondaryUrl || secondaryUrl === '#'

  return (
    <section
      className={cn('w-full bg-[var(--gv1-bg-surface)]', className)}
      data-testid="gv1-community-cta"
    >
      <div className="gv1-container">
        <div
          className="relative flex min-h-[410px] items-center overflow-hidden rounded-[var(--gv1-radius-card)] px-8 py-16 md:px-20"
        >
          <div
            className="pointer-events-none absolute inset-0"
            style={{
              background: `radial-gradient(circle at 20% 30%, rgba(255,255,255,.48), transparent 34%), radial-gradient(circle at 76% 42%, rgba(255,255,255,.24), transparent 30%), linear-gradient(125deg, ${gradientFrom} 0%, ${gradientTo} 100%)`,
            }}
            aria-hidden="true"
            data-testid="gv1-community-cta-gradient"
          />

          <div className="relative mx-auto flex max-w-3xl flex-col items-center text-center">
            <h2
              className="mb-6 text-5xl font-bold tracking-tight text-white lg:text-[64px] lg:leading-[1.05]"
              style={{ fontFamily: 'var(--gv1-font-heading)' }}
            >
              {title}
            </h2>
            <p
              className="mb-10 max-w-xl text-base leading-6 text-white/90"
              style={{ fontFamily: 'var(--gv1-font-body)' }}
            >
              {description}
            </p>

            <div className="flex flex-col items-center gap-4 sm:flex-row sm:gap-6">
              {isCtaDisabled ? (
                <button
                  disabled
                  className="inline-flex h-12 cursor-not-allowed items-center justify-center rounded-full bg-[var(--gv1-accent)] px-8 text-base font-medium text-white opacity-50"
                >
                  {ctaLabel}
                  <ArrowRight className="ml-2 size-5" />
                </button>
              ) : (
                <Link
                  href={ctaUrl}
                  className="inline-flex h-12 items-center justify-center rounded-full bg-[var(--gv1-accent)] px-8 text-base font-medium text-white transition-colors hover:bg-blue-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--gv1-accent)]"
                >
                  {ctaLabel}
                  <ArrowRight className="ml-2 size-5" />
                </Link>
              )}

              {secondaryLabel && secondaryUrl && !isSecondaryDisabled && (
                <Link
                  href={secondaryUrl}
                  className="inline-flex h-12 items-center justify-center rounded-full border border-white/30 bg-transparent px-8 text-base font-medium text-white transition-colors hover:bg-white/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 md:h-14"
                >
                  {secondaryLabel}
                </Link>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}
